/**
 * Page Entity Schemas
 *
 * Custom Pages domain — Plans 1–3 shipped a BlockNote-based content
 * template. Plan 4 pivots the content template to Puck. Tile-screen
 * template is unchanged.
 *
 * Two templates: TILE_SCREEN (tile-grid) and CONTENT (Puck Data JSON
 * with a closed eleven-component palette). Two-status version model
 * (DRAFT / PUBLISHED), at most one of each per page.
 *
 * Visibility:
 *   - PRIVATE — only fetchable by authenticated school members.
 *   - PUBLIC — fetchable without authentication via the public read endpoint.
 *
 * `LinkTargetSchema` is the discriminated union used by both the LinkCard
 * Puck component and the tile-screen editor's link-target picker. The
 * `external` variant is **https-only** — `mailto:`, `tel:`, custom schemes,
 * and `javascript:` are all rejected. Mobile renderer guards `Linking.openURL`
 * with the same constraint.
 */

import { z } from 'zod'

import { TileGridConfigSchema, TileItemSchema } from './navigation'
import { WebFeedConfigShape } from './web-feed'

// ============================================
// LinkTarget — shared between blocks and tiles
// ============================================

export const LinkTargetSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('page'), pageSlug: z.string().min(1) }),
  z.object({ kind: z.literal('feature'), featureSlug: z.string().min(1) }),
  z.object({
    kind: z.literal('external'),
    url: z
      .string()
      .url()
      .refine(u => /^https:\/\//i.test(u), {
        message: 'External link target URL must use the https: scheme'
      })
  })
])
export type LinkTarget = z.infer<typeof LinkTargetSchema>

// ============================================
// Puck content components (CONTENT template)
// ============================================
//
// Closed twelve-component palette: Heading, TextSection, RichText,
// ImageBlock, LinkCard, Embed, Columns, NavigationLink, Gallery, Video,
// Divider, WebFeed. Strict on what we own (component types + their props);
// permissive on Puck-owned metadata (`id`, `readOnly`, etc.) via
// passthrough so a Puck minor bump does not break server validation.

export const PUCK_COMPONENT_TYPES = [
  'Heading',
  'TextSection',
  'RichText',
  'ImageBlock',
  'LinkCard',
  'Embed',
  'Columns',
  'NavigationLink',
  'Gallery',
  'Video',
  'Divider',
  'WebFeed'
] as const
export const PuckComponentTypeSchema = z.enum(PUCK_COMPONENT_TYPES)
export type PuckComponentType = z.infer<typeof PuckComponentTypeSchema>

/**
 * Canonical key list for the web CONTENT Puck palette. It lives here (not in
 * the `'use client'` editor config, which a CI-gated package cannot import) so
 * the "web palette === enum" parity leg is assertable from shared-types.
 * `buildContentConfig` binds its palette to this list via a compile-time guard
 * in `components.tsx`; the parity test asserts this set === `PUCK_COMPONENT_TYPES`
 * === the mobile dispatcher's `componentMap` keys.
 */
export const WEB_CONTENT_PALETTE_KEYS = [
  'Heading',
  'TextSection',
  'RichText',
  'ImageBlock',
  'LinkCard',
  'Embed',
  'Columns',
  'NavigationLink',
  'Gallery',
  'Video',
  'Divider',
  'WebFeed'
] as const
export type WebContentPaletteKey = (typeof WEB_CONTENT_PALETTE_KEYS)[number]

/**
 * Renderer-exemption allowlist (Rule 2 / content-creation-spine contract). A
 * palette key may be listed here WITH A REASON to ship not-yet-on-a-consumer;
 * the parity test treats listed keys as intentionally exempt rather than drift.
 * Empty today — all 12 types render on every active consumer.
 */
export const RENDERER_EXEMPT: Readonly<Record<string, string>> = {}

// Hex color regex — full 6-digit form only. Optional fields validated via
// `nullable().or(...)` to allow author-set colors when present without
// forcing a value when the source has none.
const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/

export const ALIGN_VALUES = ['left', 'center', 'right'] as const
export const AlignSchema = z.enum(ALIGN_VALUES)
export type Align = z.infer<typeof AlignSchema>

export const HeadingPropsSchema = z
  .object({
    text: z.string().min(1).max(200),
    level: z.union([z.literal(1), z.literal(2), z.literal(3)]).default(2),
    // Author-intent properties: optional, only set when the source signals
    // them (e.g. inline `color:` / `text-align:` on the source element).
    // When unset, mobile/web renderers fall back to the school theme.
    align: AlignSchema.optional(),
    accentColor: z.string().regex(HEX_COLOR_RE, 'accentColor must be a 6-digit hex value').optional()
  })
  .passthrough()
export type HeadingProps = z.infer<typeof HeadingPropsSchema>

export const TextSectionPropsSchema = z
  .object({
    text: z.string().max(20_000)
  })
  .passthrough()
export type TextSectionProps = z.infer<typeof TextSectionPropsSchema>

// Rich text — Puck 0.21 built-in `richtext` field (TipTap-backed).
// Stored as HTML string. Web canvas renders inline-editable; mobile
// renders it natively via `react-native-render-html` in
// `page-renderer/blocks/rich-text.tsx`. HTML is sanitized at each
// consumer on render (mobile deny-list + https anchor gate; email
// allowlist scrub) — see the per-consumer sanitization contract in
// `docs/platform/content-creation-spine.md`. The `body` size cap below
// is the structural guard on write.
// Exported so producers can chunk to the limit rather than trip it: the
// email extractor splits oversized RichText at top-level element boundaries
// instead of emitting one body that fails validation.
export const RICH_TEXT_BODY_MAX_LENGTH = 50_000

export const RichTextPropsSchema = z
  .object({
    body: z.string().max(RICH_TEXT_BODY_MAX_LENGTH)
  })
  .passthrough()
export type RichTextProps = z.infer<typeof RichTextPropsSchema>

// Columns — a layout component with N child columns. Children are stored in
// an OWNED nested tree under `props.columns` (R4 / zones cutover): a dense
// array of length `count`, where `columns[i]` is the list of child nodes for
// the i-th column (`[]` when that column is empty). This replaces Puck's flat
// `Data.zones` map (keyed `<id>:col-<i>`).
//
// The Puck editor still speaks `zones` internally; `page-zones-adapter.ts` is
// the single place that converts between this owned tree and Puck's zones, at
// the two editor boundaries (load / save). Mobile + backend consume
// `props.columns` directly. Children may themselves be Columns (bounded
// nesting — renderers cap depth and guard cycles).
export interface ColumnsProps {
  count: 2 | 3
  columns?: ComponentNode[][]
}
// Recursive schema: `columns` holds nested `ComponentNode`s (which include
// `Columns`). `z.lazy` breaks the runtime cycle; the cast pins the public type
// to the hand-written `ColumnsProps` so DTS emit stays concrete (a plain Zod
// `infer` over the cycle would otherwise widen to `unknown`).
export const ColumnsPropsSchema = z
  .object({
    count: z.union([z.literal(2), z.literal(3)]).default(2),
    columns: z.array(z.array(z.lazy((): z.ZodTypeAny => ComponentNodeSchema))).optional()
  })
  .passthrough() as unknown as z.ZodType<ColumnsProps>

export const ImageBlockPropsSchema = z
  .object({
    url: z
      .string()
      .url()
      .refine(u => /^https:\/\//i.test(u), { message: 'Image URL must use the https: scheme' }),
    alt: z.string().max(500).default(''),
    caption: z.string().max(500).optional(),
    // Author-intent: emails commonly center hero images and float section
    // icons next to a heading. Mobile renderer maps left/right to a
    // flex-row layout when the next sibling is text; center is the default.
    align: AlignSchema.optional(),
    // Intrinsic dimensions in pixels, when known. Used as aspect-ratio
    // hints by renderers — they don't force a render size, they let the
    // renderer compute height from the available width without layout
    // shift, and let it normalize sibling images that came from the same
    // photo grid (e.g. clamping all trustee headshots to a uniform height
    // even when source heights vary by a few pixels).
    width: z.number().int().positive().max(4096).optional(),
    height: z.number().int().positive().max(4096).optional()
  })
  .passthrough()
export type ImageBlockProps = z.infer<typeof ImageBlockPropsSchema>

export const LinkCardPropsSchema = z
  .object({
    label: z.string().min(1).max(120),
    target: LinkTargetSchema
  })
  .passthrough()
export type LinkCardProps = z.infer<typeof LinkCardPropsSchema>

// ============================================
// Embed component props
// ============================================
//
// The component stores a URL constrained to https + the school's domain
// allowlist. Allowlist enforcement happens server-side at every write
// boundary; this schema only enforces shape + scheme. See
// `apps/backend/src/services/pages/embed/`.
//
// The empty string is the author-in-progress value, for the same reason as
// `WebFeedUrlDraftSchema`: the editor drops an Embed with `url: ''` and
// autosaves the whole page a second later, so refusing it failed every save
// until a URL was typed (SCH-2514). Publish refuses a URL-less Embed
// (`EMBED_URL_MISSING`), so it never reaches a published page.

export const EMBED_HEIGHT_MIN = 200
export const EMBED_HEIGHT_MAX = 1200
export const EMBED_HEIGHT_DEFAULT = 400

export const EmbedPropsSchema = z
  .object({
    url: z.union([
      z.literal(''),
      z
        .string()
        .url()
        .refine(u => /^https:\/\//i.test(u), { message: 'Embed URL must use the https: scheme' })
    ]),
    height: z.number().int().min(EMBED_HEIGHT_MIN).max(EMBED_HEIGHT_MAX).default(EMBED_HEIGHT_DEFAULT),
    title: z.string().max(255).optional()
  })
  .passthrough()
export type EmbedProps = z.infer<typeof EmbedPropsSchema>

// ============================================
// NavigationLink component props
// ============================================
//
// Links to another custom page, a built-in feature, or an external URL.
// Reuses LinkTargetSchema verbatim. The `menu-item` browse mode in the web
// editor stamps an existing LinkTarget variant on selection — there is no
// dedicated schema variant for menu items.

export const NAVIGATION_LINK_VARIANTS = ['inline', 'card'] as const
export const NavigationLinkVariantSchema = z.enum(NAVIGATION_LINK_VARIANTS)
export type NavigationLinkVariant = z.infer<typeof NavigationLinkVariantSchema>

export const NavigationLinkPropsSchema = z
  .object({
    label: z.string().min(1).max(120),
    target: LinkTargetSchema,
    variant: NavigationLinkVariantSchema.default('inline')
  })
  .passthrough()
export type NavigationLinkProps = z.infer<typeof NavigationLinkPropsSchema>

// ============================================
// Gallery component props
// ============================================
//
// References a MediaCollection by publicId. Three render variants:
//   - `link`         — text-style pressable that navigates to the gallery detail screen.
//   - `preview-card` — single CollectionCard preview, taps through to detail.
//   - `inline`       — embedded grid of media items, viewer opens in place.
//
// Permission/visibility is enforced at render time on mobile — viewers without
// access to the underlying collection see a silently hidden block.

export const GALLERY_VARIANTS = ['link', 'preview-card', 'inline'] as const
export const GalleryVariantSchema = z.enum(GALLERY_VARIANTS)
export type GalleryVariant = z.infer<typeof GalleryVariantSchema>

export const GalleryPropsSchema = z
  .object({
    collectionPublicId: z.string().min(1),
    titleOverride: z.string().max(200).optional(),
    variant: GalleryVariantSchema.default('preview-card')
  })
  .passthrough()
export type GalleryProps = z.infer<typeof GalleryPropsSchema>

// Divider — horizontal section separator. Schools' newsletters use these
// (often as bg-colored thin bars between content sections); the block lets
// authors place them in Puck-edited pages too. `color` defaults to the
// school theme accent when unset; `thickness` is in dp/px.
export const DIVIDER_THICKNESS_MIN = 1
export const DIVIDER_THICKNESS_MAX = 16
export const DIVIDER_THICKNESS_DEFAULT = 2

export const DividerPropsSchema = z
  .object({
    color: z.string().regex(HEX_COLOR_RE, 'color must be a 6-digit hex value').optional(),
    thickness: z.number().int().min(DIVIDER_THICKNESS_MIN).max(DIVIDER_THICKNESS_MAX).default(DIVIDER_THICKNESS_DEFAULT)
  })
  .passthrough()
export type DividerProps = z.infer<typeof DividerPropsSchema>

// ============================================
// Video component props
// ============================================
//
// References a Mux-backed Media row by its publicId. The block stores only
// the mediaPublicId — playback details (`muxPlaybackId`, processing state) are
// resolved at render time from the Media record so a still-PROCESSING video
// renders a placeholder and swaps to the player once the Mux webhook lands.
// `posterUrl` is an optional override; when omitted, the renderer relies on
// Mux's auto-generated thumbnail. `align` controls layout positioning on
// renderers that support it (mobile).

export const VideoPropsSchema = z
  .object({
    mediaPublicId: z.string().min(1),
    caption: z.string().max(280).optional(),
    posterUrl: z.string().url().optional(),
    align: AlignSchema.optional()
  })
  .passthrough()
export type VideoProps = z.infer<typeof VideoPropsSchema>

// ============================================
// WebFeed component props
// ============================================
//
// One external Atom/RSS feed rendered inside a CONTENT page. Stores only its
// configuration: the block resolves items live at render time through the same
// endpoint the WEB_FEED page template uses, addressed by its own `feedId`
// (several WebFeed blocks may sit on one page). The web canvas renders a
// STATIC placeholder — never live feed content — since authoring must not
// depend on a third party's uptime.
//
// `itemLimit` is the block's own window on the feed; the full-screen page
// template renders whatever the feed returned. Unlike the page template, the
// block has NO pull-to-refresh: it refreshes on mount and stale time, exactly
// as every other data-resolving block does.

export const WEB_FEED_BLOCK_ITEM_LIMIT_MIN = 1
export const WEB_FEED_BLOCK_ITEM_LIMIT_MAX = 25
export const WEB_FEED_BLOCK_ITEM_LIMIT_DEFAULT = 5

export const WebFeedPropsSchema = z
  .object({
    ...WebFeedConfigShape,
    itemLimit: z
      .number()
      .int()
      .min(WEB_FEED_BLOCK_ITEM_LIMIT_MIN)
      .max(WEB_FEED_BLOCK_ITEM_LIMIT_MAX)
      .default(WEB_FEED_BLOCK_ITEM_LIMIT_DEFAULT)
  })
  .passthrough()
export type WebFeedProps = z.infer<typeof WebFeedPropsSchema>

// Discriminated union over the allowed component types. Top-level
// Puck-owned fields (id, readOnly, etc.) accepted via passthrough.
export const ComponentNodeSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('Heading'), props: HeadingPropsSchema }).passthrough(),
  z.object({ type: z.literal('TextSection'), props: TextSectionPropsSchema }).passthrough(),
  z.object({ type: z.literal('RichText'), props: RichTextPropsSchema }).passthrough(),
  z.object({ type: z.literal('ImageBlock'), props: ImageBlockPropsSchema }).passthrough(),
  z.object({ type: z.literal('LinkCard'), props: LinkCardPropsSchema }).passthrough(),
  z.object({ type: z.literal('Embed'), props: EmbedPropsSchema }).passthrough(),
  z.object({ type: z.literal('Columns'), props: ColumnsPropsSchema }).passthrough(),
  z.object({ type: z.literal('NavigationLink'), props: NavigationLinkPropsSchema }).passthrough(),
  z.object({ type: z.literal('Gallery'), props: GalleryPropsSchema }).passthrough(),
  z.object({ type: z.literal('Video'), props: VideoPropsSchema }).passthrough(),
  z.object({ type: z.literal('Divider'), props: DividerPropsSchema }).passthrough(),
  z.object({ type: z.literal('WebFeed'), props: WebFeedPropsSchema }).passthrough()
])
// `ComponentNode` is a recursive type (Columns carries nested children in
// `props.columns`), so it is declared explicitly rather than via `z.infer`
// (which cannot express the cycle). `id` / other Puck-owned top-level keys ride
// via `.passthrough()` at parse time and are surfaced here as optional meta.
type ComponentNodeMeta = { id?: string; readOnly?: Record<string, boolean> }

/**
 * Resolve a component node's Puck id.
 *
 * **Puck stores a component's id at `props.id`, not at the node's top level**
 * (`ComponentData.props` is `WithId<Props>`, and Puck keys its zones
 * `<props.id>:<zone>`). Reading a top-level `node.id` instead silently yields
 * `undefined` for every real editor payload — which is how Columns children
 * were dropped on save before this helper existed (the zones adapter built its
 * `<id>:col-<i>` keys from the wrong field, matched nothing, and stored empty
 * columns).
 *
 * `node.id` is still accepted as a fallback: it is the shape our own fixtures
 * and hand-authored payloads have used, and honoring it costs nothing. When
 * both are present `props.id` wins — it is what Puck itself indexes on.
 */
export function resolveNodeId(node: unknown): string | undefined {
  if (!node || typeof node !== 'object') return undefined
  const candidate = node as { id?: unknown; props?: { id?: unknown } }
  const propsId = candidate.props?.id
  if (typeof propsId === 'string' && propsId.length > 0) return propsId
  return typeof candidate.id === 'string' && candidate.id.length > 0 ? candidate.id : undefined
}
export type ComponentNode = ComponentNodeMeta &
  (
    | { type: 'Heading'; props: HeadingProps }
    | { type: 'TextSection'; props: TextSectionProps }
    | { type: 'RichText'; props: RichTextProps }
    | { type: 'ImageBlock'; props: ImageBlockProps }
    | { type: 'LinkCard'; props: LinkCardProps }
    | { type: 'Embed'; props: EmbedProps }
    | { type: 'Columns'; props: ColumnsProps }
    | { type: 'NavigationLink'; props: NavigationLinkProps }
    | { type: 'Gallery'; props: GalleryProps }
    | { type: 'Video'; props: VideoProps }
    | { type: 'Divider'; props: DividerProps }
    | { type: 'WebFeed'; props: WebFeedProps }
  )

/**
 * Curated per-page background color — a theme-derived token, NOT raw hex.
 *
 * Each token names a school-theme surface role with a pre-paired readable
 * foreground, so the renderer paints a known {background, foreground} pair
 * (no contrast computation, no mid-tone failure case). Absence = the default
 * themed surface. The exact set may be refined when the web picker (palette
 * UI) and mobile renderer land — adding a token is a coordinated change here.
 */
export const PageBackgroundColorSchema = z.enum(['primary', 'secondary', 'muted', 'accent', 'card'])
export type PageBackgroundColor = z.infer<typeof PageBackgroundColorSchema>

export const ContentPageSchema = z
  .object({
    content: z.array(ComponentNodeSchema),
    root: z
      .object({
        props: z
          .object({
            title: z.string(),
            backgroundColor: PageBackgroundColorSchema.optional().describe(
              'Curated theme-derived background token for the page (absent = default themed surface)'
            )
          })
          .passthrough()
      })
      .passthrough()
  })
  // Stored CONTENT pages use the owned nested Columns tree (`props.columns`),
  // NOT Puck's flat `zones` map (R4 / zones cutover). `.passthrough()` still
  // tolerates other Puck-owned metadata, so a removed `zones` key would be
  // silently retained — only a targeted refine rejects it. Old rows carrying a
  // `zones` map must be transformed (via `fromPuckZones`) or removed BEFORE
  // this tightening deploys, or their read-validation fails (deploy-ordering
  // gate — see the plan / PR).
  .passthrough()
  .superRefine((value, ctx) => {
    if (value && typeof value === 'object' && (value as { zones?: unknown }).zones != null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['zones'],
        message:
          'Stored content must use the owned nested Columns tree (props.columns); a top-level `zones` map is not allowed.'
      })
    }

    // Walk the whole owned tree once to enforce two cross-node invariants the
    // per-node schema cannot express on its own:
    //
    //   #4 — node-id uniqueness. `toPuckZones` keys child zones by `<id>:col-<i>`,
    //        so two Columns nodes that share an id collide in the zones map and
    //        one block's children are silently dropped (last-writer-wins). Mirror
    //        the forms side (`validateFormSchema` rejects duplicate block ids).
    //   #7 — no nested `zones`. The top-level check above only guards the root;
    //        a `zones` map tucked inside a Columns node's props would survive via
    //        `.passthrough()` and persist as bloat. Reject it at any depth.
    const seenIds = new Set<string>()
    const walk = (nodes: unknown): void => {
      if (!Array.isArray(nodes)) return
      for (const node of nodes) {
        if (!node || typeof node !== 'object') continue
        const candidate = node as { id?: unknown; props?: Record<string, unknown> }
        // Read through `resolveNodeId` — Puck puts the id at `props.id`, so a
        // direct `candidate.id` read made this check inert on real payloads.
        const nodeId = resolveNodeId(candidate)
        if (nodeId) {
          if (seenIds.has(nodeId)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['content'],
              message: `Duplicate node id "${nodeId}" — node ids must be unique across the page tree.`
            })
          } else {
            seenIds.add(nodeId)
          }
        }
        const props = candidate.props
        if (props && typeof props === 'object') {
          if ((props as { zones?: unknown }).zones != null) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['content'],
              message: 'A nested `zones` map is not allowed; Columns children belong under props.columns.'
            })
          }
          const columns = (props as { columns?: unknown }).columns
          if (Array.isArray(columns)) {
            for (const column of columns) walk(column)
          }
        }
      }
    }
    walk((value as { content?: unknown }).content)
  })
export type ContentPage = z.infer<typeof ContentPageSchema>

// ============================================
// Tile-screen template content (unchanged)
// ============================================

export const TileScreenContentSchema = z
  .object({
    config: TileGridConfigSchema.optional(),
    items: z.array(TileItemSchema)
  })
  .strip()
export type TileScreenContent = z.infer<typeof TileScreenContentSchema>

// ============================================
// Feed template content
// ============================================

/**
 * FEED page content — one authored setting, the community-group section whose
 * feed the page renders. `.strip()` follows TileScreenContentSchema: there is no
 * third-party payload to preserve, so unknown keys are dropped rather than
 * persisted. Note the consequence recorded for page content generally — a future
 * field added here is silently dropped until the backend ships a rebuilt
 * shared-types package.
 *
 * The value is a Section publicId. That the section is a community group is
 * enforced backend-side at draft-save and publish (it needs a DB read), not here.
 */
export const FeedPageContentSchema = z
  .object({
    targetSectionPublicId: z.string().min(1)
  })
  .strip()
export type FeedPageContent = z.infer<typeof FeedPageContentSchema>

// ============================================
// Page + PageVersion entity contracts
// ============================================

export const PageTemplateSchema = z.enum(['TILE_SCREEN', 'CONTENT', 'FEED', 'WEB_FEED', 'ICAL_FEED'])
export type PageTemplate = z.infer<typeof PageTemplateSchema>

export const PageVisibilitySchema = z.enum(['PRIVATE', 'PUBLIC'])
export type PageVisibility = z.infer<typeof PageVisibilitySchema>

export const PageVersionStatusSchema = z.enum(['DRAFT', 'PUBLISHED'])
export type PageVersionStatus = z.infer<typeof PageVersionStatusSchema>

export const PageVersionSchema = z.object({
  publicId: z.string(),
  status: PageVersionStatusSchema,
  // `content` is template-discriminated; the use-case parses against the
  // matching schema before persistence. Wire shape is `unknown` here so that
  // TILE_SCREEN, CONTENT, and FEED pages serialize through the same envelope.
  content: z.unknown(),
  publishedAt: z.string().datetime().nullable()
})
export type PageVersion = z.infer<typeof PageVersionSchema>

export const PageSchema = z.object({
  publicId: z.string(),
  schoolPublicId: z.string(),
  slug: z.string(),
  title: z.string(),
  template: PageTemplateSchema,
  visibility: PageVisibilitySchema,
  currentVersion: PageVersionSchema.nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
})
export type Page = z.infer<typeof PageSchema>
